import { useEffect, useRef, useMemo } from 'react';
import { types } from 'models/cpm/event';
import { operationMonoDeviceErrorDesc } from 'models/cpm/synchronization';
import { ASYNC_OP_STATUS_PENDING, ASYNC_OP_STATUS_SUCCESS } from 'constants/App';
import { useIntl } from 'react-intl';
import _get from 'lodash/get';

/**
 * Hook d'écoute des events CPM pour afficher des toasts selon le statut des opérations liées.
 * @param {Object[]} events - Liste des events CPM (ex: state.cpmEvent.list)
 * @param {Object[]} operations - Liste des opérations asynchrones (ex: state.cpmOperation.list)
 * @param {function} showToast - Fonction d'affichage de toast ({ message, type })
 * @param {function} [onEventStatusUpdate] - Callback (optionnel) appelé avec (eventId, newStatus)
 */
export default function useEventsListener({
  events, operations, showToast, onEventStatusUpdate,
}) {
  // Keep already analysed events to avoid handling the same one multiple times
  const handledEventsRef = useRef(new Set());
  const { formatMessage: t } = useIntl();

  const pendingEvents = useMemo(
    () => events.filter((evt) => evt && evt.status === 'pending'),
    [events],
  );

  const processEvent = (event) => {
    if (!event || handledEventsRef.current.has(event.id)) return;

    const operationId = _get(event, 'data.operationId', null);
    if (!operationId) return;

    // Search websocket's operation link to the right websocket's event
    const operation = operations.find(({ id }) => id === operationId);
    if (!operation || operation.status === ASYNC_OP_STATUS_PENDING) return;

    handledEventsRef.current.add(event.id);

    const doorId = _get(event, 'data.door.id', null);

    if (typeof onEventStatusUpdate === 'function' && doorId) {
      onEventStatusUpdate(doorId, 'done');
    }

    if (event.type === types.doorTrigger && typeof showToast === 'function') {
      const door = _get(event, 'data.door', {});
      const isSuccess = operation.status === ASYNC_OP_STATUS_SUCCESS;
      const errorDesc = operationMonoDeviceErrorDesc(operation);

      const message = t(
        {
          id: isSuccess
            ? 'notification.doorTriggerResultSuccess'
            : 'notification.doorTriggerResultError',
        },
        {
          type: _get(door, 'type', ''),
          name: _get(door, 'name', ''),
          details: errorDesc ? t({ id: errorDesc }) : '',
        },
      );

      setTimeout(() => {
        showToast({
          message,
          type: isSuccess ? 'success' : 'alert',
        });
      }, 50);
    }
  };

  useEffect(() => {
    pendingEvents.forEach(processEvent);
  }, [pendingEvents, operations, showToast, onEventStatusUpdate, t]);
}



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/routes/show/routes/equipments/hooks/useEventsListener.js